import React, { useEffect, useState, Component } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Check, CheckCircle, Star, Lock, CreditCard, ShieldCheck, Clock, BookOpen, Headphones, FileText, AlertTriangle } from 'lucide-react';
import { SocialProofPopup } from './components/SocialProofPopup';
import { Button } from './components/ui/Button';
// --- Assets ---
const IMAGES = {
  main: 'https://cdn.magicpatterns.com/uploads/hVDsqwh4tJepWe3Ce5HmTF/20251223_0436_Promocion_Control_Financiero_remix_01kd52433gefktgqvf61rc7nzw.png',
  pricingMockup: 'https://cdn.magicpatterns.com/uploads/iXA8ASMkD4ktjAnqeRHj4i/ChatGPT_Image_23_dic_2025,_04_31_56.png',
  guaranteeBadge: 'https://cdn.magicpatterns.com/uploads/2UGU4J6G66KJfo4eP3RExN/20251223_2226_Sello_Garantia_7_Dias_simple_compose_01kd6zcbsnesws72av4rdd4pxc.png'
};
const PURCHASE_LINK = 'https://pay.hotmart.com/X103522385S?bid=1766560501373';
// --- Components ---
const FadeIn = ({
  children,
  delay = 0
}: {
  children: React.ReactNode;
  delay?: number;
}) => <motion.div initial={{
  opacity: 0,
  y: 20
}} whileInView={{
  opacity: 1,
  y: 0
}} viewport={{
  once: true
}} transition={{
  duration: 0.6,
  delay
}} data-id="element-0">
    {children}
  </motion.div>;
const Section = ({
  children,
  className = ''
}: {
  children: React.ReactNode;
  className?: string;
}) => <div className={`container mx-auto px-4 py-12 md:py-20 ${className}`} data-id="element-1">
    {children}
  </div>;
const CountdownTimer = () => {
  const [timeLeft, setTimeLeft] = useState(5 * 60); // 5 minutes in seconds
  useEffect(() => {
    if (timeLeft <= 0) return;
    const timer = setInterval(() => {
      setTimeLeft(prev => prev > 0 ? prev - 1 : 0);
    }, 1000);
    return () => clearInterval(timer);
  }, [timeLeft]);
  const minutes = Math.floor(timeLeft / 60);
  const seconds = timeLeft % 60;
  return <div className="flex items-center justify-center gap-2 bg-red-600 text-white px-6 py-3 rounded-full font-bold text-lg animate-pulse shadow-lg" data-id="element-2">
      <Clock className="w-5 h-5" data-id="element-3" />
      <span data-id="element-4">
        Oferta termina en: {String(minutes).padStart(2, '0')}:
        {String(seconds).padStart(2, '0')}
      </span>
    </div>;
};
const TestimonialCard = ({
  name,
  role,
  text,
  image,
  city
}: {
  name: string;
  role: string;
  text: string;
  image: string;
  city: string;
}) => <motion.div initial={{
  opacity: 0,
  scale: 0.95
}} whileInView={{
  opacity: 1,
  scale: 1
}} viewport={{
  once: true
}} className="bg-white p-6 rounded-2xl shadow-lg border border-gray-100 flex flex-col h-full" data-id="element-5">
    <div className="flex items-center gap-4 mb-4" data-id="element-6">
      <img src={image} alt={name} className="w-12 h-12 rounded-full object-cover border-2 border-[#FF6B35]" data-id="element-7" />
      <div data-id="element-8">
        <h4 className="font-bold text-gray-900" data-id="element-9">{name}</h4>
        <p className="text-xs text-gray-500" data-id="element-10">{city}</p>
        <div className="flex text-[#FFD166]" data-id="element-11">
          {[...Array(5)].map((_, i) => <Star key={i} size={12} fill="currentColor" data-id="element-12" />)}
        </div>
      </div>
    </div>
    <p className="text-gray-600 text-sm italic flex-grow" data-id="element-13">"{text}"</p>
    <p className="text-xs text-gray-400 mt-4 font-medium" data-id="element-14">{role}</p>
  </motion.div>;
export function App() {
  const scrollToPurchase = () => {
    const element = document.getElementById('purchase');
    element?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  return <div className="min-h-screen bg-[#FFF8F0] font-sans text-gray-900 overflow-x-hidden" data-id="element-15">
      <SocialProofPopup data-id="element-16" />

      {/* --- 1. HERO --- */}
      <header className="relative min-h-[85vh] flex items-center justify-center overflow-hidden bg-gradient-to-b from-[#003459] to-[#0077B6] text-white" data-id="element-17">
        <div className="absolute inset-0 opacity-40 mix-blend-overlay" data-id="element-18">
          <img src={IMAGES.main} alt="Background texture" className="w-full h-full object-cover blur-sm scale-110" data-id="element-19" />
        </div>

        <div className="container mx-auto px-4 relative z-10 pt-8 pb-16" data-id="element-20">
          {/* Social Proof - Top */}
          <motion.div initial={{
          opacity: 0,
          y: -20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8
        }} className="flex items-center justify-center gap-3 mb-6" data-id="element-21">
            <div className="flex -space-x-3" data-id="element-22">
              {['https://randomuser.me/api/portraits/women/44.jpg', 'https://randomuser.me/api/portraits/men/32.jpg', 'https://randomuser.me/api/portraits/women/68.jpg', 'https://randomuser.me/api/portraits/men/86.jpg', 'https://randomuser.me/api/portraits/women/22.jpg'].map((avatar, i) => <img key={i} src={avatar} alt={`Usuario ${i + 1}`} className="w-8 h-8 rounded-full border-2 border-white shadow-lg" data-id="element-23" />)}
            </div>
            <div className="text-left" data-id="element-24">
              <p className="text-xs font-bold text-white" data-id="element-25">+1.000 personas</p>
              <p className="text-[10px] text-blue-200" data-id="element-26">
                ya recuperaron el control
              </p>
            </div>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8 items-center" data-id="element-27">
            {/* Mobile Order: Text first, then Image, then Button */}
            <motion.div initial={{
            opacity: 0,
            x: -50
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 1
          }} className="text-left order-1" data-id="element-28">
              <span className="inline-block px-3 py-1 rounded-full bg-[#FF6B35]/20 border border-[#FF6B35] text-[#FF6B35] font-semibold text-xs mb-4 tracking-wider backdrop-blur-sm" data-id="element-29">
                MÉTODO FINANCIERO DE EMERGENCIA
              </span>
              <h1 className="text-4xl md:text-6xl font-bold leading-tight mb-4 text-white drop-shadow-lg" data-id="element-30">
                LLEGAR <br data-id="element-31" />
                <span className="text-[#7DD3C0] font-black tracking-tight" data-id="element-32">
                  JUSTO
                </span>{' '}
                <span className="text-[#FFD166] font-black tracking-tight" data-id="element-33">
                  OTRA VEZ
                </span>
              </h1>
              <p className="text-lg md:text-xl text-blue-100 mb-6 font-light leading-relaxed max-w-lg" data-id="element-34">
                No se trata de ganar más. Se trata de recuperar control sin
                castigarte.
              </p>
              <Button onClick={scrollToPurchase} className="w-full md:w-auto hidden md:inline-flex" data-id="element-35">
                DEJAR DE SOBREVIVIR AHORA{' '}
                <ArrowRight className="ml-2 w-5 h-5" data-id="element-36" />
              </Button>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 1,
            delay: 0.2
          }} className="relative cursor-pointer group order-2" onClick={scrollToPurchase} data-id="element-37">
              <div className="absolute inset-0 bg-[#FF6B35] rounded-xl blur-3xl opacity-20 group-hover:opacity-40 transition-opacity duration-500" data-id="element-38"></div>
              <img src={IMAGES.main} alt="Llegar Justo Otra Vez Book Cover" className="relative w-full max-w-sm mx-auto rounded-xl shadow-2xl transform transition-transform duration-500 group-hover:-translate-y-2 group-hover:rotate-1" data-id="element-39" />
            </motion.div>

            {/* Mobile CTA Button - Below Image */}
            <div className="order-3 md:hidden w-full mt-4" data-id="element-40">
              <Button onClick={scrollToPurchase} className="w-full text-lg py-4" data-id="element-41">
                DEJAR DE SOBREVIVIR AHORA{' '}
                <ArrowRight className="ml-2 w-5 h-5" data-id="element-42" />
              </Button>
            </div>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 w-full h-16 bg-gradient-to-t from-[#FFF8F0] to-transparent" data-id="element-43"></div>
      </header>

      {/* --- 2. IDENTIFICACIÓN --- */}
      <section className="bg-[#FFF8F0] py-16" data-id="element-44">
        <div className="container mx-auto px-4 max-w-4xl" data-id="element-45">
          <FadeIn data-id="element-46">
            <div className="bg-white p-8 rounded-3xl shadow-lg border border-[#FFD166]/30" data-id="element-47">
              <h2 className="text-2xl md:text-3xl font-bold font-serif text-[#003459] mb-8 text-center" data-id="element-48">
                Esto es para vos si...
              </h2>
              <div className="grid md:grid-cols-2 gap-4" data-id="element-49">
                {['Llegás a fin de mes raspando o pidiendo prestado.', 'Sentís culpa cada vez que gastás en algo para vos.', 'Te da ansiedad abrir la app del banco.', 'Tus ingresos son inestables o ajustados.', 'Estás cansada/o de intentar ahorrar y fallar.', 'Querés dejar de sobrevivir y empezar a vivir.'].map((item, i) => <div key={i} className="flex items-start gap-3 p-3 rounded-xl bg-[#FFF8F0]" data-id="element-50">
                    <div className="mt-1 w-5 h-5 rounded-full bg-[#FF6B35] flex items-center justify-center flex-shrink-0" data-id="element-51">
                      <Check size={12} className="text-white" data-id="element-52" />
                    </div>
                    <span className="text-gray-700 font-medium text-sm md:text-base" data-id="element-53">
                      {item}
                    </span>
                  </div>)}
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* --- 3. DESEO + VERDAD (UNIFICADO) --- */}
      <section className="py-16 bg-gradient-to-br from-[#003459] to-[#0077B6] text-white relative overflow-hidden" data-id="element-54">
        <div className="absolute inset-0 opacity-10 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')]" data-id="element-55"></div>
        <div className="container mx-auto px-4 max-w-4xl relative z-10" data-id="element-56">
          <FadeIn data-id="element-57">
            <div className="text-center mb-12" data-id="element-58">
              <h2 className="text-3xl md:text-4xl font-serif font-bold mb-4" data-id="element-59">
                Lo que realmente querés
              </h2>
              <p className="text-xl text-blue-100 max-w-2xl mx-auto" data-id="element-60">
                No buscás ser millonaria mañana. Buscás algo más simple y
                poderoso:{' '}
                <span className="text-[#FFD166] font-bold" data-id="element-61">Paz mental.</span>
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 items-stretch" data-id="element-62">
              <div className="bg-white/10 backdrop-blur-sm p-8 rounded-2xl border border-white/20" data-id="element-63">
                <h3 className="text-xl font-bold text-[#FFD166] mb-4" data-id="element-64">
                  Tu Deseo Oculto
                </h3>
                <ul className="space-y-3" data-id="element-65">
                  <li className="flex items-center gap-2 text-sm md:text-base" data-id="element-66">
                    <Check size={16} className="text-[#FFD166]" data-id="element-67" /> Cobrar sin
                    entrar en pánico.
                  </li>
                  <li className="flex items-center gap-2 text-sm md:text-base" data-id="element-68">
                    <Check size={16} className="text-[#FFD166]" data-id="element-69" /> Gastar sin
                    culpa porque decidís vos.
                  </li>
                  <li className="flex items-center gap-2 text-sm md:text-base" data-id="element-70">
                    <Check size={16} className="text-[#FFD166]" data-id="element-71" /> Sentirte
                    capaz y en control.
                  </li>
                </ul>
              </div>

              <div className="bg-white/10 backdrop-blur-sm p-8 rounded-2xl border border-white/20" data-id="element-72">
                <h3 className="text-xl font-bold text-[#FF6B35] mb-4" data-id="element-73">
                  La Verdad
                </h3>
                <p className="text-sm md:text-base leading-relaxed mb-4" data-id="element-74">
                  No gastás mal porque seas irresponsable. Gastás mal porque
                  nadie te enseñó a manejar la plata cuando estás sobreviviendo.
                </p>
                <p className="text-sm md:text-base font-bold italic" data-id="element-75">
                  El sistema tradicional no sirve para tu realidad. Necesitás un
                  método distinto.
                </p>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* --- 4. QUÉ ES EL MÉTODO --- */}
      <section className="py-16 bg-white" data-id="element-76">
        <div className="container mx-auto px-4 max-w-5xl" data-id="element-77">
          <div className="grid md:grid-cols-2 gap-12 items-center" data-id="element-78">
            <FadeIn data-id="element-79">
              <img src={IMAGES.main} alt="Llegar Justo Otra Vez" className="w-full max-w-md mx-auto rounded-xl shadow-2xl transform rotate-2 hover:rotate-0 transition-transform duration-500" data-id="element-80" />
            </FadeIn>
            <FadeIn delay={0.2} data-id="element-81">
              <h2 className="text-3xl md:text-4xl font-serif font-bold text-[#003459] mb-6" data-id="element-82">
                ¿Qué es{' '}
                <span className="text-[#FF6B35]" data-id="element-83">Llegar Justo Otra Vez</span>?
              </h2>
              <p className="text-lg text-gray-700 mb-6 leading-relaxed" data-id="element-84">
                Es un sistema práctico, humano y realista diseñado para personas
                que viven al día. No es un curso de finanzas aburrido con
                términos que nadie entiende.
              </p>
              <p className="text-lg text-gray-700 mb-6 leading-relaxed" data-id="element-85">
                Es una guía de supervivencia emocional y financiera que te lleva
                de la ansiedad al control en 30 días, usando lo que ya tenés.
              </p>
              <div className="flex items-center gap-2 text-[#0077B6] font-bold" data-id="element-86">
                <CheckCircleIcon data-id="element-87" /> Sin planillas complejas.
              </div>
              <div className="flex items-center gap-2 text-[#0077B6] font-bold mt-2" data-id="element-88">
                <CheckCircleIcon data-id="element-89" /> Sin dejar de vivir.
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* --- 5. LA PIEZA QUE FALTABA --- */}
      <section className="py-16 bg-[#FFF8F0] relative overflow-hidden" data-id="element-90">
        <div className="absolute inset-0 opacity-5 bg-[url('https://www.transparenttextures.com/patterns/paper.png')]" data-id="element-91"></div>
        <div className="container mx-auto px-4 max-w-6xl relative z-10" data-id="element-92">
          <FadeIn data-id="element-93">
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-center text-[#003459] mb-12" data-id="element-94">
              Cómo funciona el cambio
            </h2>
          </FadeIn>

          <div className="grid md:grid-cols-3 gap-8" data-id="element-95">
            {[{
            icon: '🪣',
            title: 'El balde agujereado',
            learn: 'Aprendés a tapar las fugas invisibles.',
            change: 'Dejás de sangrar dinero sin darte cuenta.'
          }, {
            icon: '🚗',
            title: 'El parabrisas tapado',
            learn: 'Aprendés a ver tus números con claridad.',
            change: 'Dejás de chocar por no ver el camino.'
          }, {
            icon: '🤿',
            title: 'Respirar bajo el agua',
            learn: 'Aprendés técnicas para bajar la ansiedad.',
            change: 'Recuperás el aire y la calma mental.'
          }].map((meta, i) => <FadeIn delay={i * 0.2} key={i} data-id="element-96">
                <div className="bg-white p-8 rounded-2xl shadow-lg border-b-4 border-[#FF6B35] h-full hover:transform hover:-translate-y-1 transition-transform duration-300" data-id="element-97">
                  <div className="text-4xl mb-4 text-center" data-id="element-98">{meta.icon}</div>
                  <h3 className="font-bold text-xl text-[#003459] mb-4 text-center" data-id="element-99">
                    {meta.title}
                  </h3>
                  <div className="space-y-3" data-id="element-100">
                    <p className="text-sm text-gray-600" data-id="element-101">
                      <span className="font-bold text-[#FF6B35]" data-id="element-102">
                        Aprendés:
                      </span>{' '}
                      {meta.learn}
                    </p>
                    <p className="text-sm text-gray-600" data-id="element-103">
                      <span className="font-bold text-[#0077B6]" data-id="element-104">Cambia:</span>{' '}
                      {meta.change}
                    </p>
                  </div>
                </div>
              </FadeIn>)}
          </div>
          <p className="text-center text-gray-500 mt-8 italic font-medium" data-id="element-105">
            El método actúa, no solo motiva.
          </p>
        </div>
      </section>

      {/* --- 6. TODO LO QUE RECIBÍS (UNIFICADO) --- */}
      <section className="py-16 bg-gradient-to-br from-[#003459] to-[#00507A] text-white" data-id="element-106">
        <div className="container mx-auto px-4 max-w-4xl" data-id="element-107">
          <FadeIn data-id="element-108">
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-center mb-12" data-id="element-109">
              Todo lo que recibís hoy
            </h2>

            <div className="bg-white/10 backdrop-blur-md rounded-3xl p-8 md:p-12 border border-white/20 shadow-2xl" data-id="element-110">
              <div className="space-y-8" data-id="element-111">
                {/* Main Product */}
                <div className="flex items-start gap-4" data-id="element-112">
                  <div className="bg-[#FF6B35] p-3 rounded-xl text-white flex-shrink-0" data-id="element-113">
                    <BookOpen size={24} data-id="element-114" />
                  </div>
                  <div data-id="element-115">
                    <h3 className="text-xl font-bold text-[#FFD166]" data-id="element-116">
                      Ebook: Llegar Justo Otra Vez
                    </h3>
                    <p className="text-blue-100 text-sm" data-id="element-117">
                      El método completo paso a paso para recuperar el control
                      en 30 días.
                    </p>
                  </div>
                </div>

                {/* Bonus 1 */}
                <div className="flex items-start gap-4" data-id="element-118">
                  <div className="bg-[#00B4D8] p-3 rounded-xl text-white flex-shrink-0" data-id="element-119">
                    <ShieldCheck size={24} data-id="element-120" />
                  </div>
                  <div data-id="element-121">
                    <span className="text-xs font-bold bg-[#00B4D8] px-2 py-0.5 rounded text-white mb-1 inline-block" data-id="element-122">
                      BONUS 1
                    </span>
                    <h3 className="text-lg font-bold text-white" data-id="element-123">
                      Primeros 7 días sin pánico
                    </h3>
                    <p className="text-blue-200 text-sm" data-id="element-124">
                      Guía de choque para frenar la sangría inmediata.
                    </p>
                  </div>
                </div>

                {/* Bonus 2 */}
                <div className="flex items-start gap-4" data-id="element-125">
                  <div className="bg-[#00B4D8] p-3 rounded-xl text-white flex-shrink-0" data-id="element-126">
                    <FileText size={24} data-id="element-127" />
                  </div>
                  <div data-id="element-128">
                    <span className="text-xs font-bold bg-[#00B4D8] px-2 py-0.5 rounded text-white mb-1 inline-block" data-id="element-129">
                      BONUS 2
                    </span>
                    <h3 className="text-lg font-bold text-white" data-id="element-130">
                      Planilla AntiAnsiedad (Editable)
                    </h3>
                    <p className="text-blue-200 text-sm" data-id="element-131">
                      Simple, visual y automática. Sin fórmulas raras.
                    </p>
                  </div>
                </div>

                {/* Bonus 3 */}
                <div className="flex items-start gap-4" data-id="element-132">
                  <div className="bg-[#00B4D8] p-3 rounded-xl text-white flex-shrink-0" data-id="element-133">
                    <Headphones size={24} data-id="element-134" />
                  </div>
                  <div data-id="element-135">
                    <span className="text-xs font-bold bg-[#00B4D8] px-2 py-0.5 rounded text-white mb-1 inline-block" data-id="element-136">
                      BONUS 3
                    </span>
                    <h3 className="text-lg font-bold text-white" data-id="element-137">
                      Audio "Cobré y no entro en pánico"
                    </h3>
                    <p className="text-blue-200 text-sm" data-id="element-138">
                      Reset emocional de 10 minutos para escuchar antes de
                      gastar.
                    </p>
                  </div>
                </div>

                {/* Bonus 4 */}
                <div className="flex items-start gap-4" data-id="element-139">
                  <div className="bg-[#00B4D8] p-3 rounded-xl text-white flex-shrink-0" data-id="element-140">
                    <AlertTriangle size={24} data-id="element-141" />
                  </div>
                  <div data-id="element-142">
                    <span className="text-xs font-bold bg-[#00B4D8] px-2 py-0.5 rounded text-white mb-1 inline-block" data-id="element-143">
                      BONUS 4
                    </span>
                    <h3 className="text-lg font-bold text-white" data-id="element-144">
                      Lista Negra de Errores Invisibles
                    </h3>
                    <p className="text-blue-200 text-sm" data-id="element-145">
                      Lo que te hace perder plata sin darte cuenta.
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-8 pt-8 border-t border-white/10 text-center" data-id="element-146">
                <p className="text-sm text-blue-200 italic" data-id="element-147">
                  "Los bonos se entregan de forma progresiva para acompañarte
                  sin abrumarte."
                </p>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* --- 7. TESTIMONIOS (REDUCIDO) --- */}
      <section className="py-16 bg-white" data-id="element-148">
        <div className="container mx-auto px-4 max-w-6xl" data-id="element-149">
          <h2 className="text-3xl font-serif font-bold text-center text-[#003459] mb-12" data-id="element-150">
            Historias reales
          </h2>
          <div className="grid md:grid-cols-3 gap-6" data-id="element-151">
            <TestimonialCard name="Camila R." city="Buenos Aires" role="Freelancer, 29 años" text="Siempre pensé que ganaba poco, pero el problema era el desorden. Con el audio 'Cobré y no entro en pánico' pude frenar la ansiedad. Por primera vez llegué al día 20 con plata." image="https://randomuser.me/api/portraits/women/44.jpg" data-id="element-152" />
            <TestimonialCard name="Javier M." city="Córdoba" role="Administrativo, 34 años" text="La planilla AntiAnsiedad es lo mejor. Simple, visual, sin cosas raras. Dejé de tenerle miedo al home banking. Dormí tranquilo después de meses." image="https://randomuser.me/api/portraits/men/32.jpg" data-id="element-153" />
            <TestimonialCard name="Valentina S." city="Rosario" role="Emprendedora, 31 años" text="Estaba cansada de sentirme culpable. Este método me enseñó a diferenciar supervivencia de vida. El concepto de 'dejar de sangrar' me cambió la cabeza." image="https://randomuser.me/api/portraits/women/68.jpg" data-id="element-154" />
          </div>
        </div>
      </section>

      {/* --- 8. GARANTÍA (NUEVA) --- */}
      <section className="py-12 bg-[#FFF8F0] border-t border-[#FFD166]/30" data-id="element-155">
        <div className="container mx-auto px-4 max-w-3xl" data-id="element-156">
          <FadeIn data-id="element-157">
            <div className="flex flex-col md:flex-row items-center gap-8 bg-white p-8 rounded-3xl shadow-sm border border-gray-100" data-id="element-158">
              <img src={IMAGES.guaranteeBadge} alt="Garantía 7 Días" className="w-32 md:w-40 flex-shrink-0" data-id="element-159" />
              <div className="text-center md:text-left" data-id="element-160">
                <h3 className="text-2xl font-bold text-[#003459] mb-3" data-id="element-161">
                  Garantía sin vueltas
                </h3>
                <p className="text-gray-600 leading-relaxed" data-id="element-162">
                  Estamos tan seguros de que este método te va a ayudar, que si
                  sentís que no es para vos, te devolvemos el 100% de tu dinero.
                  Tenés 7 días para probarlo sin riesgo. Si no te convence, nos
                  escribís y hacemos el reembolso. Así de simple.
                </p>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* --- 9. BLOQUE FINAL DE COMPRA --- */}
      <section id="purchase" className="py-16 bg-[#003459] text-white relative overflow-hidden" data-id="element-163">
        <div className="absolute inset-0 opacity-20 bg-[url('https://www.transparenttextures.com/patterns/stardust.png')]" data-id="element-164"></div>
        <div className="container mx-auto px-4 max-w-4xl relative z-10 text-center" data-id="element-165">
          <FadeIn data-id="element-166">
            <h2 className="text-3xl md:text-5xl font-serif font-bold mb-8" data-id="element-167">
              Tu tranquilidad empieza hoy
            </h2>

            <div className="bg-white text-gray-900 rounded-3xl p-8 md:p-12 shadow-2xl max-w-2xl mx-auto relative overflow-hidden" data-id="element-168">
              <div className="flex justify-center mb-6" data-id="element-169">
                <img src={IMAGES.pricingMockup} alt="Pack Completo" className="w-64 md:w-80 rounded" data-id="element-170" />
              </div>
              <h3 className="text-2xl font-bold mb-2 text-[#003459]" data-id="element-171">
                LLEGAR JUSTO OTRA VEZ
              </h3>
              <p className="text-gray-500 mb-6 font-medium" data-id="element-172">
                + Todos los bonos incluidos
              </p>

              {/* Pricing */}
              <div className="mb-6" data-id="element-173">
                <div className="flex items-center justify-center gap-4 mb-4" data-id="element-174">
                  <span className="text-2xl text-gray-400 line-through" data-id="element-175">
                    $49.990
                  </span>
                  <span className="bg-red-100 text-red-600 px-3 py-1 rounded-full text-sm font-bold" data-id="element-176">
                    -60%
                  </span>
                </div>
                <div className="flex items-baseline justify-center gap-2" data-id="element-177">
                  <span className="text-5xl md:text-7xl font-bold text-green-600" data-id="element-178">
                    $19.990
                  </span>
                  <span className="text-gray-400 font-medium" data-id="element-179">ARS</span>
                </div>
                <p className="text-sm text-gray-500 mt-2" data-id="element-180">
                  Acceso inmediato y de por vida
                </p>
              </div>

              {/* Animated Purchase Button */}
              <motion.a href={PURCHASE_LINK} target="_blank" rel="noopener noreferrer" className="block w-full bg-[#FF6B35] hover:bg-[#E85D2A] text-white font-bold text-xl py-5 rounded-xl shadow-lg relative overflow-hidden group" animate={{
              boxShadow: ['0 0 0 rgba(255, 107, 53, 0)', '0 0 20px rgba(255, 107, 53, 0.5)', '0 0 0 rgba(255, 107, 53, 0)']
            }} transition={{
              duration: 2,
              repeat: Infinity,
              ease: 'easeInOut'
            }} data-id="element-181">
                <span className="relative z-10 flex items-center justify-center gap-2" data-id="element-182">
                  DESCARGAR AHORA <ArrowRight size={24} data-id="element-183" />
                </span>
                <motion.div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-30" animate={{
                x: ['-100%', '100%']
              }} transition={{
                duration: 2,
                repeat: Infinity,
                ease: 'linear'
              }} data-id="element-184" />
              </motion.a>

              {/* Payment Trust Block */}
              <div className="mt-4 mb-6 bg-gray-50 rounded-lg p-4 border border-gray-200" data-id="element-185">
                <div className="flex items-center justify-center gap-2 mb-2" data-id="element-186">
                  <Lock size={14} className="text-gray-600" data-id="element-187" />
                  <p className="text-xs text-gray-700 font-medium" data-id="element-188">
                    Pago 100% seguro procesado por Hotmart
                  </p>
                </div>
                <div className="flex flex-col items-center gap-1" data-id="element-189">
                  <div className="flex items-center gap-2" data-id="element-190">
                    <Check size={12} className="text-green-600" data-id="element-191" />
                    <p className="text-xs text-gray-600" data-id="element-192">
                      Crédito · Débito · Transferencia
                    </p>
                  </div>
                  <div className="flex items-center gap-2" data-id="element-193">
                    <Check size={12} className="text-green-600" data-id="element-194" />
                    <p className="text-xs text-gray-600" data-id="element-195">
                      Medios de pago locales disponibles
                    </p>
                  </div>
                </div>
              </div>

              {/* Trust Badges - Enhanced */}
              <div className="grid grid-cols-3 gap-4 pt-6 border-t border-gray-200" data-id="element-196">
                <div className="flex flex-col items-center gap-2" data-id="element-197">
                  <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center" data-id="element-198">
                    <ShieldCheck size={24} className="text-green-600" data-id="element-199" />
                  </div>
                  <span className="text-xs font-semibold text-gray-700 text-center" data-id="element-200">
                    Pago Seguro
                  </span>
                </div>
                <div className="flex flex-col items-center gap-2" data-id="element-201">
                  <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center" data-id="element-202">
                    <Clock size={24} className="text-blue-600" data-id="element-203" />
                  </div>
                  <span className="text-xs font-semibold text-gray-700 text-center" data-id="element-204">
                    Acceso Inmediato
                  </span>
                </div>
                <div className="flex flex-col items-center gap-2" data-id="element-205">
                  <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center" data-id="element-206">
                    <Star size={24} className="text-purple-600" fill="currentColor" data-id="element-207" />
                  </div>
                  <span className="text-xs font-semibold text-gray-700 text-center" data-id="element-208">
                    Garantía de Calidad
                  </span>
                </div>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* --- FOOTER --- */}
      <footer className="bg-[#002845] text-blue-200 py-12 border-t border-white/10" data-id="element-209">
        <div className="container mx-auto px-4 text-center" data-id="element-210">
          <p className="mb-4 font-medium" data-id="element-211">
            © {new Date().getFullYear()} Llegar Justo Otra Vez. Todos los
            derechos reservados.
          </p>
          <p className="text-sm opacity-60 max-w-2xl mx-auto" data-id="element-212">
            Este producto no garantiza la obtención de resultados. Las
            referencias al desempeño de una determinada estrategia no debe ser
            interpretada como una garantía de resultados.
          </p>
        </div>
      </footer>
    </div>;
}
function CheckCircleIcon() {
  return <CheckCircle className="w-5 h-5 flex-shrink-0" data-id="element-213" />;
}